// src/components/admin/compensation/PayGroupsTab.js
// Pay Groups Tab - Manage standardized salary structures by job level/grade

import React, { useState, useEffect } from 'react';
import {
    Plus,
    Edit,
    Trash2,
    Users,
    DollarSign,
    TrendingUp,
    Search,
    Filter,
    Eye,
    UserPlus,
    UserMinus,
    Zap,
    AlertTriangle,
    CheckCircle,
    XCircle
} from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import payGroupsService from '../../../services/payGroups.service';
import PayGroupModal from './PayGroupModal';
import AssignEmployeesModal from './AssignEmployeesModal';
import FlaggedEmployeesPanel from './FlaggedEmployeesPanel';

const PayGroupsTab = () => {
    const { theme } = useTheme();
    const [loading, setLoading] = useState(true);
    const [payGroups, setPayGroups] = useState([]);
    const [stats, setStats] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterActive, setFilterActive] = useState('all'); // 'all', 'active', 'inactive'
    const [selectedPayGroup, setSelectedPayGroup] = useState(null);
    const [showPayGroupModal, setShowPayGroupModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

    // Auto-assignment state
    const [showAutoAssignResults, setShowAutoAssignResults] = useState(false);
    const [autoAssignResults, setAutoAssignResults] = useState(null);
    const [autoAssigning, setAutoAssigning] = useState(false);

    // Unassign state
    const [unassigning, setUnassigning] = useState(false);
    const [showUnassignResults, setShowUnassignResults] = useState(false);
    const [unassignResults, setUnassignResults] = useState(null);

    useEffect(() => {
        fetchPayGroups();
        fetchStats();
    }, []);

    const fetchPayGroups = async () => {
        try {
            setLoading(true);
            const filters = {};
            if (filterActive !== 'all') {
                filters.is_active = filterActive === 'active';
            }

            const response = await payGroupsService.getAllPayGroups(filters);
            if (response.success) {
                setPayGroups(response.payGroups || []);
            }
        } catch (error) {
            console.error('Error fetching pay groups:', error);
            alert('Failed to load pay groups');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await payGroupsService.getPayGroupStats();
            if (response.success) {
                setStats(response.stats);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const handleCreatePayGroup = () => {
        setSelectedPayGroup(null);
        setModalMode('create');
        setShowPayGroupModal(true);
    };

    const handleEditPayGroup = (payGroup) => {
        setSelectedPayGroup(payGroup);
        setModalMode('edit');
        setShowPayGroupModal(true);
    };

    const handleDeletePayGroup = async (payGroup) => {
        if (payGroup.employee_count > 0) {
            alert(`Cannot delete "${payGroup.name}": ${payGroup.employee_count} employee(s) are assigned. Please reassign employees first.`);
            return;
        }

        if (!window.confirm(`Are you sure you want to delete "${payGroup.name}"?`)) {
            return;
        }

        try {
            const response = await payGroupsService.deletePayGroup(payGroup.id);
            if (response.success) {
                alert('Pay group deleted successfully');
                fetchPayGroups();
                fetchStats();
            }
        } catch (error) {
            console.error('Error deleting pay group:', error);
            alert(error.response?.data?.error || 'Failed to delete pay group');
        }
    };

    const handleAssignEmployees = (payGroup) => {
        setSelectedPayGroup(payGroup);
        setShowAssignModal(true);
    };

    const handlePayGroupSaved = () => {
        setShowPayGroupModal(false);
        setSelectedPayGroup(null);
        fetchPayGroups();
        fetchStats();
    };

    const handleEmployeesAssigned = () => {
        setShowAssignModal(false);
        setSelectedPayGroup(null);
        fetchPayGroups();
        fetchStats();
    };

    const handleFlagResolved = () => {
        fetchPayGroups();
        fetchStats();
    };

    const handleAutoAssign = async () => {
        if (!window.confirm('Auto-assign all unassigned employees to pay groups based on their CTC?\n\nThis will:\n- Assign employees whose CTC falls within a pay group range\n- Flag conflicts for manual review\n- Apply salary structure templates')) {
            return;
        }

        setAutoAssigning(true);
        try {
            const response = await payGroupsService.autoAssignPayGroups({
                mode: 'unassigned',
                apply_salary_structure: true
            });

            if (response.success) {
                setAutoAssignResults(response);
                setShowAutoAssignResults(true);
                fetchPayGroups();
                fetchStats();
            }
        } catch (error) {
            console.error('Auto-assign error:', error);
            alert(error.response?.data?.error || 'Failed to auto-assign employees');
        } finally {
            setAutoAssigning(false);
        }
    };

    const handleUnassignAllCompanyWide = async () => {
        if (!window.confirm('⚠️ WARNING: This will unassign ALL employees company-wide from their pay groups!\n\nThis action will:\n- Remove all employees from their pay groups\n- Clear their salary structure data\n- This cannot be undone easily\n\nAre you absolutely sure you want to continue?')) {
            return;
        }

        setUnassigning(true);
        try {
            const response = await payGroupsService.unassignAllCompanyWide({
                confirmation: true,
                clear_salary_data: true
            });

            if (response.success) {
                setUnassignResults(response);
                setShowUnassignResults(true);
                fetchPayGroups();
                fetchStats();
                alert(`Successfully unassigned ${response.unassigned_count} employee(s)`);
            }
        } catch (error) {
            console.error('Unassign all error:', error);
            alert(error.response?.data?.error || 'Failed to unassign employees');
        } finally {
            setUnassigning(false);
        }
    };

    const filteredPayGroups = payGroups.filter(pg =>
        pg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pg.level.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pg.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">Loading pay groups...</div>
            </div>
        );
    }

    return (
        <div className={`space-y-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {/* Header Section */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold">Pay Groups</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Manage standardized salary structures by job level/grade
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleAutoAssign}
                        disabled={autoAssigning || stats?.unassigned_employees === 0}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                            autoAssigning || stats?.unassigned_employees === 0
                                ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                    >
                        <Zap className="w-4 h-4" />
                        {autoAssigning ? 'Auto-Assigning...' : 'Auto-Assign Employees'}
                    </button>
                    <button
                        onClick={handleUnassignAllCompanyWide}
                        disabled={unassigning || stats?.total_employees_assigned === 0}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                            unassigning || stats?.total_employees_assigned === 0
                                ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                : 'bg-red-600 text-white hover:bg-red-700'
                        }`}
                        title="Unassign all employees from pay groups company-wide"
                    >
                        <UserMinus className="w-4 h-4" />
                        {unassigning ? 'Unassigning...' : 'Unassign All Company-Wide'}
                    </button>
                    <button
                        onClick={handleCreatePayGroup}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Create Pay Group
                    </button>
                </div>
            </div>

            {/* Statistics Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Total Pay Groups</p>
                                <p className="text-2xl font-bold">{stats.total_pay_groups || 0}</p>
                            </div>
                            <DollarSign className="w-8 h-8 text-blue-500" />
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Active Groups</p>
                                <p className="text-2xl font-bold">{stats.active_pay_groups || 0}</p>
                            </div>
                            <TrendingUp className="w-8 h-8 text-green-500" />
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Employees Assigned</p>
                                <p className="text-2xl font-bold">{stats.total_employees_assigned || 0}</p>
                            </div>
                            <Users className="w-8 h-8 text-purple-500" />
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Unassigned</p>
                                <p className="text-2xl font-bold">{stats.unassigned_employees || 0}</p>
                            </div>
                            <UserPlus className="w-8 h-8 text-orange-500" />
                        </div>
                    </div>
                </div>
            )}

            {/* Flagged Employees Panel */}
            <FlaggedEmployeesPanel onResolved={handleFlagResolved} />

            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                    <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search pay groups..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                            focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={() => setFilterActive('all')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            filterActive === 'all'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setFilterActive('active')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            filterActive === 'active'
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                    >
                        Active
                    </button>
                    <button
                        onClick={() => setFilterActive('inactive')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            filterActive === 'inactive'
                                ? 'bg-gray-600 text-white'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                    >
                        Inactive
                    </button>
                </div>
            </div>

            {/* Pay Groups List */}
            {filteredPayGroups.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                    <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">
                        No Pay Groups Found
                    </h3>
                    <p className="text-gray-500 dark:text-gray-500 mb-6">
                        {searchQuery ? 'Try adjusting your search' : 'Create your first pay group to get started'}
                    </p>
                    {!searchQuery && (
                        <button
                            onClick={handleCreatePayGroup}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            <Plus className="w-4 h-4" />
                            Create Pay Group
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {filteredPayGroups.map((payGroup) => (
                        <div
                            key={payGroup.id}
                            className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow p-6"
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-lg font-bold">{payGroup.name}</h3>
                                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                                            payGroup.is_active
                                                ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                        }`}>
                                            {payGroup.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">{payGroup.description}</p>
                                </div>
                            </div>

                            {/* Salary Range */}
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Level</span>
                                    <span className="font-semibold">{payGroup.level}</span>
                                </div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">CTC Range</span>
                                    <span className="font-semibold">
                                        {formatCurrency(payGroup.min_ctc)} - {formatCurrency(payGroup.max_ctc)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Employees</span>
                                    <span className="flex items-center gap-1 font-semibold">
                                        <Users className="w-4 h-4" />
                                        {payGroup.employee_count || 0}
                                    </span>
                                </div>
                            </div>

                            {/* Components Preview */}
                            {payGroup.salary_components && payGroup.salary_components.length > 0 && (
                                <div className="mb-4">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                        Salary Components ({payGroup.salary_components.length})
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {payGroup.salary_components.slice(0, 3).map((comp, idx) => (
                                            <span
                                                key={idx}
                                                className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded"
                                            >
                                                {comp.name} ({comp.percentage}%)
                                            </span>
                                        ))}
                                        {payGroup.salary_components.length > 3 && (
                                            <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                                                +{payGroup.salary_components.length - 3} more
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <button
                                    onClick={() => handleAssignEmployees(payGroup)}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors"
                                    title="Assign Employees"
                                >
                                    <UserPlus className="w-4 h-4" />
                                    <span className="text-sm">Assign</span>
                                </button>
                                <button
                                    onClick={() => handleEditPayGroup(payGroup)}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                    title="Edit Pay Group"
                                >
                                    <Edit className="w-4 h-4" />
                                    <span className="text-sm">Edit</span>
                                </button>
                                <button
                                    onClick={() => handleDeletePayGroup(payGroup)}
                                    className="flex items-center justify-center px-3 py-2 bg-red-50 dark:bg-red-900 text-red-600 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-800 transition-colors"
                                    title="Delete Pay Group"
                                    disabled={payGroup.employee_count > 0}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modals */}
            {showPayGroupModal && (
                <PayGroupModal
                    isOpen={showPayGroupModal}
                    onClose={() => {
                        setShowPayGroupModal(false);
                        setSelectedPayGroup(null);
                    }}
                    onSave={handlePayGroupSaved}
                    payGroup={selectedPayGroup}
                    mode={modalMode}
                />
            )}

            {showAssignModal && (
                <AssignEmployeesModal
                    isOpen={showAssignModal}
                    onClose={() => {
                        setShowAssignModal(false);
                        setSelectedPayGroup(null);
                    }}
                    onAssign={handleEmployeesAssigned}
                    payGroup={selectedPayGroup}
                />
            )}

            {/* Auto-Assignment Results Modal */}
            {showAutoAssignResults && autoAssignResults && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className={`w-full max-w-2xl mx-4 rounded-lg shadow-xl ${
                        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                    }`}>
                        {/* Header */}
                        <div className={`px-6 py-4 border-b ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                            <h3 className="text-xl font-semibold flex items-center gap-2">
                                <Zap className="w-5 h-5 text-purple-500" />
                                Auto-Assignment Results
                            </h3>
                        </div>

                        {/* Summary Cards */}
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className={`p-4 rounded-lg border ${
                                    theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-blue-50 border-blue-200'
                                }`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <Users className="w-4 h-4 text-blue-500" />
                                        <span className="text-sm font-medium">Total</span>
                                    </div>
                                    <p className="text-2xl font-bold">{autoAssignResults.summary.total}</p>
                                </div>

                                <div className={`p-4 rounded-lg border ${
                                    theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-green-50 border-green-200'
                                }`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                        <span className="text-sm font-medium">Assigned</span>
                                    </div>
                                    <p className="text-2xl font-bold text-green-600">{autoAssignResults.summary.assigned}</p>
                                </div>

                                <div className={`p-4 rounded-lg border ${
                                    theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-yellow-50 border-yellow-200'
                                }`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                                        <span className="text-sm font-medium">Flagged</span>
                                    </div>
                                    <p className="text-2xl font-bold text-yellow-600">{autoAssignResults.summary.flagged}</p>
                                </div>

                                <div className={`p-4 rounded-lg border ${
                                    theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
                                }`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <XCircle className="w-4 h-4 text-gray-500" />
                                        <span className="text-sm font-medium">Skipped</span>
                                    </div>
                                    <p className="text-2xl font-bold text-gray-600">{autoAssignResults.summary.skipped}</p>
                                </div>
                            </div>

                            {/* Message */}
                            <div className={`p-4 rounded-lg ${
                                theme === 'dark' ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-800'
                            }`}>
                                <p className="text-sm">{autoAssignResults.message}</p>
                                {autoAssignResults.summary.flagged > 0 && (
                                    <p className="text-sm mt-2">
                                        <strong>Note:</strong> {autoAssignResults.summary.flagged} employee(s) need manual review.
                                        Check the "Flagged Employees" section to resolve conflicts.
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className={`px-6 py-4 border-t flex justify-end ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                            <button
                                onClick={() => setShowAutoAssignResults(false)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PayGroupsTab;
